import { useState, useMemo, useEffect } from "react";
import { Search, ChevronUp, ChevronDown, Filter, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 25;

export default function DirectoryList({ units, loading, selectedUnit, onUnitSelect, floor, wing }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField, setSortField] = useState("unit_id");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  // Reset page on filter/search/sort changes or floor change
  useEffect(() => { setPage(1); }, [search, statusFilter, sortField, sortDir, floor, wing]);

  // Auto-scroll to selected unit
  useEffect(() => {
    if (selectedUnit) {
      const el = document.getElementById(`dir-row-${selectedUnit}`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedUnit]);

  const filtered = useMemo(() => {
    let result = [...units];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (u) =>
          u.unit_id.toLowerCase().includes(s) ||
          u.owners.some((o) => o.name.toLowerCase().includes(s)) ||
          u.owners.some((o) => o.phone.toLowerCase().includes(s))
      );
    }
    if (statusFilter) {
      result = result.filter((u) => u.occupancy_status === statusFilter);
    }
    result.sort((a, b) => {
      let va = a[sortField] ?? "";
      let vb = b[sortField] ?? "";
      // Natural sort for unit IDs like H-1, H-2, H-10
      if (sortField === "unit_id") {
        const numA = parseInt(va.replace(/\D+/g, ""));
        const numB = parseInt(vb.replace(/\D+/g, ""));
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortDir === "asc" ? numA - numB : numB - numA;
        }
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [units, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }) =>
    sortField === field ? (
      sortDir === "asc" ? <ChevronUp size={12} className="text-[#9C7C54]" /> : <ChevronDown size={12} className="text-[#9C7C54]" />
    ) : (
      <ChevronDown size={12} className="text-[#D1D1D1]" />
    );

  return (
    <div className="flex flex-col h-full" data-testid="directory-list">
      {/* Search + Filter */}
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-[#E5E5E5] dark:border-[#272A30] space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input
            data-testid="directory-search"
            type="search"
            inputMode="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            placeholder="Search unit, owner, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 text-base sm:text-sm border border-[#E5E5E5] dark:border-[#272A30] bg-[#F9F8F6] dark:bg-[#0F1115] text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={12} className="text-[#737373]" />
          <div className="flex gap-1.5">
            {["", "occupied", "vacant"].map((s) => (
              <button
                key={s}
                data-testid={`status-filter-${s || "all"}`}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-[11px] tracking-wide transition-colors rounded-sm ${
                  statusFilter === s
                    ? "bg-[#9C7C54] text-white"
                    : "bg-[#F5F3F0] dark:bg-[#272A30] text-[#737373] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA]"
                }`}
              >
                {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <span className="ml-auto text-[10px] text-[#737373]">{filtered.length} units</span>        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#F5F3F0] dark:bg-[#0F1115] border-b border-[#E5E5E5] dark:border-[#272A30] px-3 sm:px-4 py-2 grid grid-cols-12 gap-2">
          <button
            onClick={() => handleSort("unit_id")}
            className="col-span-3 flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373] hover:text-[#9C7C54] transition-colors"
            data-testid="sort-unit-id"
          >
            Unit <SortIcon field="unit_id" />
          </button>
          <button
            onClick={() => handleSort("size_sqft")}
            className="col-span-3 flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373] hover:text-[#9C7C54] transition-colors"
            data-testid="sort-size"
          >
            Size <SortIcon field="size_sqft" />
          </button>
          <div className="col-span-3 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">Status</div>
          <div className="col-span-3 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">Owner</div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#9C7C54] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#737373]">
            <Search size={32} className="opacity-30 mb-3" />
            <p className="text-sm">No units found</p>
          </div>
        ) : (
          paginated.map((unit, i) => (
            <div
              key={unit.unit_id}
              id={`dir-row-${unit.unit_id}`}
              data-testid={`dir-row-${unit.unit_id}`}
              onClick={() => onUnitSelect(unit.unit_id)}
              className={`px-3 sm:px-4 py-3.5 sm:py-3 grid grid-cols-12 gap-2 cursor-pointer border-b border-[#E5E5E5] dark:border-[#272A30] transition-colors duration-150 stagger-child animate-fade-in active:bg-[#F5EDE0]/60 dark:active:bg-[#2A2318]/60 ${
                selectedUnit === unit.unit_id
                  ? "bg-[#F5EDE0] dark:bg-[#2A2318] border-l-2 border-l-[#9C7C54]"
                  : "hover:bg-[#F9F8F6] dark:hover:bg-[#1A1D24]"
              }`}
              style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}
            >
              <div className="col-span-3 text-sm font-semibold text-[#1A1A1A] dark:text-[#EAEAEA] truncate">
                {unit.unit_id}
              </div>
              <div className="col-span-3 text-xs text-[#737373] self-center truncate">
                {unit.size_sqft > 0 ? `${unit.size_sqft.toFixed(0)} sf` : "—"}
              </div>
              <div className="col-span-3 self-center">
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${
                    unit.occupancy_status === "occupied" ? "badge-occupied" : "badge-vacant"
                  }`}
                >
                  {unit.occupancy_status === "occupied" ? "Occ." : "Vacant"}
                </span>
              </div>
              <div className="col-span-3 text-[11px] text-[#737373] self-center truncate">
                {unit.owners.length > 0 ? unit.owners[0].name : "—"}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-3 sm:px-4 py-2 border-t border-[#E5E5E5] dark:border-[#272A30] bg-[#F9F8F6] dark:bg-[#0F1115]" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
          <span className="text-[10px] text-[#737373]">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              data-testid="page-prev-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 sm:w-6 sm:h-6 flex items-center justify-center text-[#737373] hover:text-[#9C7C54] disabled:opacity-40 transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} className="sm:w-3.5 sm:h-3.5" />
            </button>
            <span className="text-[11px] sm:text-[10px] font-semibold text-[#1A1A1A] dark:text-[#EAEAEA] px-1">
              {page}/{totalPages}
            </span>
            <button
              data-testid="page-next-btn"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 sm:w-6 sm:h-6 flex items-center justify-center text-[#737373] hover:text-[#9C7C54] disabled:opacity-40 transition-colors"
              aria-label="Next page"
            >
              <ChevronRight size={16} className="sm:w-3.5 sm:h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
